import React, { useState } from 'react';
import {
  GitBranch,
  Plus,
} from 'lucide-react';
import type { Book, EditionTreeResponse } from '../../../shared/types/index.js';
import { EditionDiagramView } from './edition-tree/EditionDiagramView.js';
import { EditionTimelineView } from './edition-tree/EditionTimelineView.js';
import { EditionCompareView } from './edition-tree/EditionCompareView.js';
import { EditionDetailPanel } from './edition-tree/EditionDetailPanel.js';

interface EditionTreeVisualizerProps {
  treeData: EditionTreeResponse;
  currentBookId: string;
  onSelectBook?: (book: Book) => void;
  onRequestCreateEdition?: (parentBook: Book) => void;
}

export const EditionTreeVisualizer: React.FC<EditionTreeVisualizerProps> = ({
  treeData,
  currentBookId,
  onSelectBook,
  onRequestCreateEdition,
}) => {
  const [activeTab, setActiveTab] = useState<'tree' | 'timeline' | 'compare'>('tree');
  const [treeViewMode, setTreeViewMode] = useState<'diagram' | 'list'>('diagram');
  const [copiedUibn, setCopiedUibn] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<Book>(treeData.currentBook);
  const [isDetailExpanded, setIsDetailExpanded] = useState<boolean>(true);

  const handleCopyUibn = (uibn: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(uibn);
    setCopiedUibn(uibn);
    setTimeout(() => setCopiedUibn(null), 2000);
  };

  const handleSelectNode = (node: Book) => {
    setSelectedNode(node);
    setIsDetailExpanded(true);
  };

  // Find parent book info of selected node if applicable
  const selectedParentBook = selectedNode.parentBookId
    ? treeData.editions.find((b) => b.id === selectedNode.parentBookId) || null
    : null;

  return (
    <div className="flex flex-col h-full bg-slate-50/60 rounded-none sm:rounded-2xl overflow-hidden border-0 sm:border sm:border-slate-200 min-h-0">
      {/* Visualizer Top Header */}
      <div className="bg-white border-b border-slate-200 px-3 py-2.5 sm:px-5 sm:py-3.5 shrink-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center p-1.5 bg-indigo-50 text-indigo-700 rounded-lg shrink-0">
                <GitBranch className="w-4 h-4" />
              </span>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 leading-snug">
                Pohon Silsilah Edisi (Edition Tree)
              </h3>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 truncate max-w-xl">
              Naskah: <span className="font-semibold text-slate-700">"{treeData.rootBook.title}"</span>
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {onRequestCreateEdition && (
              <button
                type="button"
                id="btn-tree-create-edition"
                onClick={() => onRequestCreateEdition(selectedNode)}
                className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg transition shadow-xs cursor-pointer shrink-0"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Terbitkan Edisi</span>
              </button>
            )}

            {/* Tab navigation */}
            <div className="flex items-center bg-slate-100 p-0.5 rounded-lg text-xs font-medium text-slate-600 border border-slate-200 overflow-x-auto max-w-full">
              <button
                type="button"
                onClick={() => setActiveTab('tree')}
                className={`px-2.5 sm:px-3 py-1 rounded-md transition whitespace-nowrap text-xs cursor-pointer ${
                  activeTab === 'tree'
                    ? 'bg-white text-indigo-700 shadow-xs font-semibold'
                    : 'hover:text-slate-900'
                }`}
              >
                Bagan Silsilah
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('timeline')}
                className={`px-2.5 sm:px-3 py-1 rounded-md transition whitespace-nowrap text-xs cursor-pointer ${
                  activeTab === 'timeline'
                    ? 'bg-white text-indigo-700 shadow-xs font-semibold'
                    : 'hover:text-slate-900'
                }`}
              >
                Garis Waktu ({treeData.totalEditions})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('compare')}
                className={`px-2.5 sm:px-3 py-1 rounded-md transition whitespace-nowrap text-xs cursor-pointer ${
                  activeTab === 'compare'
                    ? 'bg-white text-indigo-700 shadow-xs font-semibold'
                    : 'hover:text-slate-900'
                }`}
              >
                Perbandingan
              </button>
            </div>
          </div>
        </div>

        {/* Tree Stats Bar */}
        <div className="flex items-center gap-2 sm:gap-4 mt-2 pt-2 border-t border-slate-100 text-[11px] sm:text-xs text-slate-600 overflow-x-auto no-scrollbar whitespace-nowrap">
          <div className="flex items-center gap-1 shrink-0">
            <span className="font-semibold text-slate-900">Total:</span>
            <span className="px-1.5 py-0.2 bg-indigo-50 text-indigo-700 rounded-full font-bold">
              {treeData.totalEditions} Edisi
            </span>
          </div>
          <span className="text-slate-300">•</span>
          <div className="flex items-center gap-1 shrink-0">
            <span className="font-semibold text-slate-900">Induk:</span>
            <span className="text-slate-700 font-medium">
              {treeData.rootBook.edition || 'Edisi 1'} ({treeData.rootBook.publicationYear})
            </span>
          </div>
          <span className="text-slate-300">•</span>
          <div className="flex items-center gap-1 shrink-0">
            <span className="font-semibold text-slate-900">Penulis:</span>
            <span className="text-slate-700 truncate max-w-[120px] sm:max-w-none">{treeData.rootBook.authorName}</span>
          </div>
          <span className="text-slate-300">•</span>
          <div className="flex items-center gap-1 shrink-0">
            <span className="font-semibold text-slate-900">Penerbit:</span>
            <span className="text-slate-700 truncate max-w-[120px] sm:max-w-none">{treeData.rootBook.publisherName}</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto p-2 sm:p-5 min-h-0 bg-slate-50/50">
        {/* Tab 1: Bagan Silsilah (Tree) */}
        {activeTab === 'tree' && (
          <EditionDiagramView
            tree={treeData.tree}
            currentBookId={currentBookId}
            selectedNode={selectedNode}
            onSelectNode={handleSelectNode}
            copiedUibn={copiedUibn}
            onCopyUibn={handleCopyUibn}
            onOpenBookDetail={onSelectBook}
            onRequestCreateEdition={onRequestCreateEdition}
            treeViewMode={treeViewMode}
            onTreeViewModeChange={setTreeViewMode}
          />
        )}

        {/* Tab 2: Garis Waktu (Timeline) */}
        {activeTab === 'timeline' && (
          <EditionTimelineView
            editions={treeData.editions}
            currentBookId={currentBookId}
            selectedNode={selectedNode}
            onSelectNode={handleSelectNode}
            onOpenBookDetail={onSelectBook}
          />
        )}

        {/* Tab 3: Matriks Perbandingan (Compare) */}
        {activeTab === 'compare' && (
          <EditionCompareView
            editions={treeData.editions}
            currentBookId={currentBookId}
            selectedNode={selectedNode}
            onSelectNode={handleSelectNode}
            copiedUibn={copiedUibn}
            onCopyUibn={handleCopyUibn}
            onOpenBookDetail={onSelectBook}
            onRequestCreateEdition={onRequestCreateEdition}
          />
        )}
      </div>

      {/* DEDICATED READABLE DETAIL AREA (AREA DETAIL EDISI TERPILIH) */}
      <EditionDetailPanel
        selectedNode={selectedNode}
        currentBookId={currentBookId}
        isDetailExpanded={isDetailExpanded}
        onToggleExpanded={() => setIsDetailExpanded(!isDetailExpanded)}
        copiedUibn={copiedUibn}
        onCopyUibn={handleCopyUibn}
        selectedParentBook={selectedParentBook}
        onOpenBookDetail={onSelectBook}
        onRequestCreateEdition={onRequestCreateEdition}
      />
    </div>
  );
};
